/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { css } from '@emotion/react'
import { Modal, Spin, Input } from 'antd'
import {
  LoadingOutlined,
  SearchOutlined,
  EnvironmentOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import req from '@sportninja/common/api/request'
import type { Venue } from '../hooks/useSchedulerLocations'

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
    padding: '0',
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
    backgroundColor: colors.SECONDARY_300,
  },
}

interface VenuePickerModalProps {
  isOpen: boolean
  onClose: () => void
  competitionId: string
  alreadyAttachedVenueIds: string[]
  onVenuesAttached: (venueIds: string[]) => Promise<void>
  onCreateNew: () => void
}

const VenuePickerModal: React.FC<VenuePickerModalProps> = ({
  isOpen,
  onClose,
  competitionId,
  alreadyAttachedVenueIds,
  onVenuesAttached,
  onCreateNew,
}) => {
  const [allVenues, setAllVenues] = useState<Venue[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // Fetch all org-tree venues when modal opens
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
      setSelectedIds(new Set())
      return
    }

    const fetchAllVenues = async () => {
      setLoading(true)
      try {
        const { data } = await req('/venues', {
          query: { schedule_id: competitionId },
        })
        setAllVenues(data || [])
      } catch {
        setAllVenues([])
      } finally {
        setLoading(false)
      }
    }

    fetchAllVenues()
  }, [isOpen, competitionId])

  // Filter out already-attached venues, then apply search
  const availableVenues = useMemo(() => {
    const attachedSet = new Set(alreadyAttachedVenueIds)
    const filtered = allVenues.filter((v) => !attachedSet.has(v.id))

    if (!searchQuery.trim()) {
      return filtered
    }

    const q = searchQuery.toLowerCase()
    return filtered.filter(
      (v) =>
        v.name?.toLowerCase().includes(q) ||
        v.name_full?.toLowerCase().includes(q) ||
        v.address?.city?.toLowerCase().includes(q)
    )
  }, [allVenues, alreadyAttachedVenueIds, searchQuery])

  const toggleVenue = useCallback((venueId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(venueId)) {
        next.delete(venueId)
      } else {
        next.add(venueId)
      }
      return next
    })
  }, [])

  const handleConfirm = useCallback(async () => {
    if (selectedIds.size === 0) {
      return
    }

    setSubmitting(true)
    try {
      await onVenuesAttached(Array.from(selectedIds))
      onClose()
    } catch {
      // Error handling is done by the parent
    } finally {
      setSubmitting(false)
    }
  }, [selectedIds, onVenuesAttached, onClose])

  const allOrgVenuesAttached = useMemo(() => {
    const attachedSet = new Set(alreadyAttachedVenueIds)
    return (
      allVenues.length > 0 &&
      allVenues.every((v) => attachedSet.has(v.id)) &&
      !searchQuery.trim()
    )
  }, [allVenues, alreadyAttachedVenueIds, searchQuery])

  const footer = useMemo(
    () => (
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
        `}
      >
        <button
          type='button'
          onClick={onCreateNew}
          css={css`
            display: flex;
            align-items: center;
            gap: 6px;
            background: transparent;
            border: 1px solid ${colors.SECONDARY_100};
            border-radius: 8px;
            padding: 8px 16px;
            cursor: pointer;
            font-family: 'Inter', sans-serif;
            font-size: 14px;
            font-weight: 500;
            color: ${colors.NEUTRAL_50};
            transition: all 0.2s ease;

            &:hover {
              border-color: ${colors.PRIMARY_300};
              color: ${colors.PRIMARY_300};
            }
          `}
        >
          <PlusOutlined />
          Create New Location
        </button>
        <div
          css={css`
            display: flex;
            gap: 12px;
          `}
        >
          <button
            type='button'
            onClick={onClose}
            disabled={submitting}
            css={css`
              background: transparent;
              border: 1px solid ${colors.SECONDARY_100};
              border-radius: 8px;
              padding: 8px 20px;
              cursor: pointer;
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              font-weight: 500;
              color: ${colors.NEUTRAL_50};
              transition: all 0.2s ease;

              &:hover {
                border-color: ${colors.NEUTRAL_50};
              }

              &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
              }
            `}
          >
            Cancel
          </button>
          <button
            type='button'
            onClick={handleConfirm}
            disabled={selectedIds.size === 0 || submitting}
            css={css`
              background: ${colors.PRIMARY_300};
              border: none;
              border-radius: 8px;
              padding: 8px 20px;
              cursor: pointer;
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              font-weight: 600;
              color: ${colors.SECONDARY_500};
              transition: all 0.2s ease;

              &:hover:not(:disabled) {
                background: ${colors.PRIMARY_200};
              }

              &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
              }
            `}
          >
            {submitting ? (
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 14, color: colors.SECONDARY_500 }}
                    spin
                  />
                }
              />
            ) : (
              `Add Selected${
                selectedIds.size > 0 ? ` (${selectedIds.size})` : ''
              }`
            )}
          </button>
        </div>
      </div>
    ),
    [onClose, onCreateNew, handleConfirm, selectedIds.size, submitting]
  )

  if (!isOpen) {
    return null
  }

  return (
    <Modal
      css={css`
        .ant-modal-content {
          max-height: 80vh;
          display: flex;
          flex-direction: column;
          border-radius: 8px;
        }
        .ant-modal-body {
          flex: 1;
          overflow-y: auto;
          min-height: 0;
        }
      `}
      width={600}
      title={
        <span
          css={css`
            font-family: 'Inter', sans-serif;
            font-size: 18px;
            font-weight: 600;
            color: ${colors.NEUTRAL_0};
          `}
        >
          Select Locations
        </span>
      }
      open={isOpen}
      onCancel={onClose}
      footer={footer}
      centered
      styles={modalStyles}
      maskClosable={false}
      closeIcon={
        <span
          css={css`
            color: ${colors.NEUTRAL_50};
            font-size: 16px;
          `}
        >
          &times;
        </span>
      }
    >
      {/* Search Input */}
      <div
        css={css`
          padding: 16px;
        `}
      >
        <Input
          prefix={
            <SearchOutlined
              css={css`
                color: ${colors.NEUTRAL_100};
              `}
            />
          }
          placeholder='Search venues...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
          css={css`
            && {
              background-color: ${colors.SECONDARY_400};
              border: 1px solid
                ${(colors as Record<string, string>).SECONDARY_125};
              border-radius: 8px;
              height: 40px;

              .ant-input {
                background-color: transparent;
                color: ${colors.NEUTRAL_0};
                font-family: 'Inter', sans-serif;
                font-size: 14px;

                &::placeholder {
                  color: ${colors.NEUTRAL_100};
                }
              }

              .ant-input-clear-icon {
                color: ${colors.NEUTRAL_100};
              }

              &:hover,
              &:focus-within {
                border-color: ${colors.PRIMARY_300};
              }
            }
          `}
        />
      </div>

      {/* Venue List */}
      <div
        css={css`
          padding: 0 16px 16px;
          min-height: 200px;
          max-height: 400px;
          overflow-y: auto;
        `}
      >
        {loading ? (
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 48px;
            `}
          >
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
            <span
              css={css`
                margin-left: 12px;
                color: ${colors.NEUTRAL_50};
                font-family: 'Inter', sans-serif;
                font-size: 14px;
              `}
            >
              Loading venues...
            </span>
          </div>
        ) : allOrgVenuesAttached ? (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 48px;
              text-align: center;
            `}
          >
            <EnvironmentOutlined
              css={css`
                font-size: 36px;
                color: ${colors.NEUTRAL_100};
                margin-bottom: 12px;
              `}
            />
            <span
              css={css`
                color: ${colors.NEUTRAL_50};
                font-family: 'Inter', sans-serif;
                font-size: 14px;
              `}
            >
              All organization venues have been added
            </span>
          </div>
        ) : availableVenues.length === 0 ? (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 48px;
              text-align: center;
            `}
          >
            <SearchOutlined
              css={css`
                font-size: 36px;
                color: ${colors.NEUTRAL_100};
                margin-bottom: 12px;
              `}
            />
            <span
              css={css`
                color: ${colors.NEUTRAL_50};
                font-family: 'Inter', sans-serif;
                font-size: 14px;
              `}
            >
              No venues found matching &quot;{searchQuery}&quot;
            </span>
          </div>
        ) : (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 4px;
            `}
          >
            {availableVenues.map((venue) => {
              const isSelected = selectedIds.has(venue.id)
              return (
                <button
                  key={venue.id}
                  type='button'
                  onClick={() => toggleVenue(venue.id)}
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    width: 100%;
                    padding: 12px;
                    background: ${isSelected
                      ? `${colors.PRIMARY_300}15`
                      : 'transparent'};
                    border: 1px solid
                      ${isSelected
                        ? colors.PRIMARY_300
                        : (colors as Record<string, string>).SECONDARY_125};
                    border-radius: 8px;
                    cursor: pointer;
                    text-align: left;
                    transition: all 0.15s ease;

                    &:hover {
                      background: ${isSelected
                        ? `${colors.PRIMARY_300}20`
                        : `${colors.SECONDARY_200}`};
                      border-color: ${isSelected
                        ? colors.PRIMARY_300
                        : colors.SECONDARY_100};
                    }
                  `}
                >
                  {/* Checkbox */}
                  <div
                    css={css`
                      width: 20px;
                      height: 20px;
                      border-radius: 4px;
                      border: 2px solid
                        ${isSelected ? colors.PRIMARY_300 : colors.NEUTRAL_100};
                      background: ${isSelected
                        ? colors.PRIMARY_300
                        : 'transparent'};
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      flex-shrink: 0;
                      transition: all 0.15s ease;
                    `}
                  >
                    {isSelected && (
                      <svg
                        width='12'
                        height='12'
                        viewBox='0 0 12 12'
                        fill='none'
                      >
                        <path
                          d='M10 3L4.5 8.5L2 6'
                          stroke={colors.SECONDARY_500}
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    )}
                  </div>

                  {/* Venue info */}
                  <div
                    css={css`
                      flex: 1;
                      min-width: 0;
                    `}
                  >
                    <div
                      css={css`
                        font-family: 'Inter', sans-serif;
                        font-size: 14px;
                        font-weight: 500;
                        color: ${colors.NEUTRAL_0};
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                      `}
                    >
                      {venue.name || venue.name_full}
                    </div>
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        margin-top: 2px;
                      `}
                    >
                      {venue.address?.city && (
                        <span
                          css={css`
                            font-family: 'Inter', sans-serif;
                            font-size: 12px;
                            color: ${colors.NEUTRAL_100};
                          `}
                        >
                          {venue.address.city}
                        </span>
                      )}
                      {venue.facilities?.length > 0 && (
                        <span
                          css={css`
                            font-family: 'Inter', sans-serif;
                            font-size: 12px;
                            color: ${colors.NEUTRAL_100};
                          `}
                        >
                          {venue.facilities.length} surface
                          {venue.facilities.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default VenuePickerModal
